// traceWebResearch.js — what the scan window's agents READ from the open web,
// reconstructed from raw request bodies (the same hydration mechanic as the
// Files and Linear X-rays: tool_use/tool_result blocks per prompt, deduped
// across each session's cumulative re-sends, aggregated per source and actor).
//
// This is the INPUT-corpus sibling of the output-artifact X-rays: Files/Linear
// reconstruct what an agent PRODUCED; this reconstructs what it CONSUMED — the
// pages it fetched (the processed text that actually entered its context) and
// the searches that led there. Two op kinds carry that:
//
//   search — a query and the results it returned (a ranked list of {url,title}).
//   fetch  — a specific URL and the CONTENT the agent pulled from it (capped).
//
// Grounded in real traffic: the dominant tools are the built-in WebSearch /
// WebFetch, plus the Anthropic server `web_search`, and a long tail of MCP
// research tools (workspace web_fetch, firecrawl scrape/search, deepwiki
// ask_question). We key on the tool's ACTION verb + argument shape, not one
// fixed name, so a new research server is picked up without a code change —
// but conservatively (an explicit verb set), because over-matching a generic
// "search" tool (search_mcp_registry, search_session_transcripts, Grep) would
// pollute the corpus with non-web reads. A page fetched through Bash `curl` is
// out of scope here, exactly like shell file edits are out of scope for the
// Files tree.
//
// The fetch op deliberately reuses traceFileTree's READ-observation shape (see
// pageDoc): a page has no writes — the agent never authors it — only reads, so
// reconstructFile renders the observed content and, when two fetches of the
// same URL disagree, marks a "drift" version. For a web page that drift is a
// real signal: the page CHANGED between two agents' (or two sessions') reads.

// A page's captured content. Web pages are the whole point of this view — we
// want to see what the agent saw — so the cap is generous relative to Linear's
// prose cap, but still bounded: this is a per-window client scan and a window
// can touch many pages.
const FETCH_CONTENT_CAP = 8000;
// A search op keeps at most this many result links.
const SEARCH_RESULT_CAP = 12;
// A search result's raw text is scanned for links up to this length.
const SEARCH_TEXT_CAP = 12000;

// Research VERBS we accept, by op kind. Deliberately explicit — see the header
// note on why a generic /search/ or /fetch/ is unsafe.
const FETCH_ACTION_RE =
  /^(webfetch|web_fetch|fetch_url|url_fetch|fetch_page|firecrawl_scrape|firecrawl_fetch|scrape|scrape_url|navigate|open_url|visit_url|read_url|get_url)$/;
const SEARCH_ACTION_RE =
  /^(websearch|web_search|web_search_call|search_web|brave_search|google_search|duckduckgo_search|tavily_search|tavily_web_search|exa_search|firecrawl_search|deepwiki_search|ask_question)$/;

// A url-bearing argument (fetch) and a query-bearing argument (search).
const URL_KEYS = ["url", "uri", "link", "href", "page", "page_url", "target"];
const QUERY_KEYS = ["query", "q", "question", "search_query", "searchQuery", "prompt"];

const URL_SCAN_RE = /https?:\/\/[^\s"'<>)\]}]+/g;

// Split an MCP tool name into (server, action): "mcp__firecrawl__firecrawl_scrape"
// → ["firecrawl", "firecrawl_scrape"]. A non-MCP name (WebFetch, WebSearch,
// web_search) returns { server: null, action: <name> } so the built-in and
// server tools flow through the same classifier.
export function parseResearchTool(name) {
  const s = String(name || "");
  if (!s) return null;
  const segs = s.split("__");
  if (segs[0] === "mcp") {
    if (segs.length < 3) return null;
    return { server: segs.slice(1, -1).join("__"), action: segs[segs.length - 1].toLowerCase() };
  }
  return { server: null, action: s.toLowerCase() };
}

function firstString(input, keys) {
  for (const k of keys) {
    const v = input?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

// The engine label an op is attributed to: the MCP server segment, else a
// stable name for the built-in / server tool. Labels only, for grouping.
function engineOf(parsed) {
  if (parsed.server) return parsed.server;
  if (parsed.action === "web_search" || parsed.action === "web_search_call") return "web_search";
  if (parsed.action === "web__run") return "web";
  return parsed.action; // webfetch | websearch
}

// Codex's hosted web tool ships several queries in ONE call: search_query is
// an array of { q } entries (observed shape; a bare string is tolerated).
function searchQueryList(input) {
  const v = input?.search_query;
  if (typeof v === "string" && v.trim()) return [v.trim()];
  if (!Array.isArray(v)) return [];
  return v
    .map((e) => (typeof e === "string" ? e : typeof e?.q === "string" ? e.q : null))
    .filter((q) => q && q.trim());
}

// kind: "fetch" (pulled a page's content) | "search" (ran a query) | null.
// A fetch REQUIRES a resolvable URL; a search REQUIRES a query string —
// without them the op can't be pinned to a source and is skipped.
export function classifyResearchTool(parsed, input) {
  const url = firstString(input, URL_KEYS);
  if (FETCH_ACTION_RE.test(parsed.action)) return url ? "fetch" : null;
  // A plain WebFetch/navigate whose verb we matched but url lives elsewhere:
  // already handled — url is required above.
  if (SEARCH_ACTION_RE.test(parsed.action)) {
    // ask_question tools carry the query under `question`; require any query key.
    return firstString(input, QUERY_KEYS) ? "search" : null;
  }
  // web__run: Codex's web tool, reached through code-mode cells. Search-shaped
  // input only — the same "requires a query" rule as every other search verb.
  if (parsed.action === "web__run") {
    return searchQueryList(input).length ? "search" : null;
  }
  return null;
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((p) => {
        if (typeof p === "string") return p;
        if (p?.type === "web_search_result") return ""; // structured — handled separately
        return p?.text || "";
      })
      .join("\n");
  }
  return "";
}

// Same contract as traceFileTree/traceLinearOps: is_error is normalized to
// isError, and Claude Code surfaces some failures only as a marker in content.
function resultErrored(result) {
  if (!result) return false;
  if (result.isError) return true;
  return contentText(result.content).includes("<tool_use_error>");
}

function capInfo(s, max) {
  const t = typeof s === "string" ? s : "";
  return { text: t.length > max ? t.slice(0, max) : t, truncated: t.length > max };
}

function hostOf(url) {
  try {
    return new URL(url).host || null;
  } catch {
    // A bare "example.com/x" without scheme — take the leading authority.
    const m = /^([a-z0-9.-]+\.[a-z]{2,})(?:[/:?#]|$)/i.exec(String(url || ""));
    return m ? m[1].toLowerCase() : null;
  }
}

// Normalize a URL for identity: drop the fragment and a trailing slash so the
// same page fetched with/without "#section" or a "/" collapses to one source.
// Query strings are KEPT — they often select the actual document.
function normalizeUrl(url) {
  const s = String(url || "").trim();
  if (!s) return s;
  try {
    const u = new URL(s);
    u.hash = "";
    let out = u.toString();
    // Drop a trailing slash (root included) so "host.com" and "host.com/"
    // collapse to one page identity. A path with a query keeps its "?…".
    if (out.endsWith("/")) out = out.slice(0, -1);
    return out;
  } catch {
    return s.split("#")[0].replace(/\/$/, "");
  }
}

// djb2 — stable synthetic dedup key (same as the sibling libs).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// The first markdown heading of fetched content — a cheap page title when the
// tool didn't give us one. Falls back to null (the URL carries identity).
function titleFromContent(text) {
  if (!text) return null;
  const m = /^#{1,3}\s+(.+?)\s*$/m.exec(text);
  return m ? m[1].slice(0, 200) : null;
}

// Pull a result list out of a search's result payload. Two shapes observed:
//   - Anthropic web_search_tool_result: content is an array of
//     { type: "web_search_result", url, title }.
//   - Everything else: formatted text — scan it for links, keep order/dedup.
function searchResults(result) {
  const out = [];
  const seen = new Set();
  const push = (url, title) => {
    const n = normalizeUrl(url);
    if (!n || seen.has(n) || out.length >= SEARCH_RESULT_CAP) return;
    seen.add(n);
    out.push({ url: n, title: title || null });
  };
  const raw = result?.content;
  if (Array.isArray(raw)) {
    for (const p of raw) {
      if (p && typeof p === "object" && p.type === "web_search_result" && p.url) {
        push(p.url, typeof p.title === "string" ? p.title : null);
      }
    }
  }
  if (out.length < SEARCH_RESULT_CAP) {
    const text = contentText(raw).slice(0, SEARCH_TEXT_CAP);
    const matches = text.match(URL_SCAN_RE) || [];
    for (const u of matches) push(u.replace(/[.,;]+$/, ""), null);
  }
  return out;
}

// One prompt's turns → its web-research ops. Op shape:
//   fetch:  { key, kind:"fetch", engine, url, host, title, prompt, content,
//             contentTruncated, ok }
//   search: { key, kind:"search", engine, query, results:[{url,title}],
//             resultCount, ok }
// `content` on a fetch is the processed page text the agent actually saw
// (capped). `prompt` is the extraction instruction the agent passed WITH the
// url (WebFetch's `prompt`) — i.e. what it was looking for on that page.
export function extractWebResearchOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const parsed = parseResearchTool(b.name);
      if (!parsed) continue;
      const input = b.input && typeof b.input === "object" ? b.input : {};
      const kind = classifyResearchTool(parsed, input);
      if (!kind) continue;
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);
      const engine = engineOf(parsed);

      if (kind === "fetch") {
        const url = normalizeUrl(firstString(input, URL_KEYS));
        const c = capInfo(ok && result ? contentText(result.content) : "", FETCH_CONTENT_CAP);
        const key = b.id || `synth:fetch:${hashString(url)}`;
        ops.push({
          key, kind, engine, url, host: hostOf(url),
          title: titleFromContent(c.text),
          prompt: firstString(input, ["prompt", "instruction", "goal"]),
          content: c.text || null, contentTruncated: c.truncated, ok,
        });
      } else {
        const query = firstString(input, QUERY_KEYS) || searchQueryList(input).join(" · ");
        const results = ok ? searchResults(result) : [];
        const key = b.id || `synth:search:${hashString(engine + "|" + query)}`;
        ops.push({
          key, kind, engine, query,
          results, resultCount: results.length, ok,
        });
      }
    }
  }
  return ops;
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

// Flatten per-prompt extractions into the window's real ops — the same mechanic
// as traceFileTree.collectWindowOps / traceLinearOps.collectWindowLinearOps:
// prompts oldest-first, an op attributed to the FIRST prompt whose body carries
// it; later cumulative re-sightings in the same session collapse onto it.
export function collectWindowWebResearchOps(promptEvents) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = new Set();
  const flat = [];
  for (const p of sorted) {
    for (const op of p.ops || []) {
      const dedupKey = `${p.sessionId || ""}|${op.key}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  return flat;
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

// The window's web-research activity — source-, page-, query-, and actor-centric:
//   hosts:   [{ host, pages:[url…], fetches, searches }]
//   pages:   [{ url, host, title, fetches, sessions, readers:[{id,name,n}],
//              viaQueries:[query…], ops (chronological), drift(bool) }]
//   queries: [{ query, engine, resultCount, results:[{url,title}], sessions,
//              readers:[{id,name,n}], leadTo:[url…] (result urls also fetched
//              in-window) }]
//   actors:  [{ id, name, fetches, searches, hosts, sessions }] — an actor is
//            the ENDPOINT (the machine a person's agent runs on); friendly
//            labels resolve in the component (useLabels), like the sibling views.
//   totals:  { pages, hosts, fetches, searches, queries, actors }
export function buildWebResearchActivity(flatOps) {
  const pages = new Map();
  const queries = new Map();
  const actors = new Map();
  let fetches = 0;
  let searches = 0;

  const bump = (side, id, name) => {
    const prev = side.get(id) || { id, name: name || shortId(id), n: 0 };
    prev.n += 1;
    side.set(id, prev);
  };
  const actorOf = (op) => {
    let a = actors.get(op.endpointId);
    if (!a) {
      a = { id: op.endpointId, name: op.endpointName || shortId(op.endpointId), fetches: 0, searches: 0, hosts: new Set(), sessions: new Set() };
      actors.set(op.endpointId, a);
    }
    a.sessions.add(op.sessionId);
    return a;
  };

  for (const op of flatOps || []) {
    const actor = actorOf(op);
    if (op.kind === "fetch") {
      if (!op.url) continue;
      let page = pages.get(op.url);
      if (!page) {
        page = { url: op.url, host: op.host || null, title: null, fetches: 0, readers: new Map(), sessions: new Set(), viaQueries: new Set(), ops: [] };
        pages.set(op.url, page);
      }
      if (!page.title && op.title) page.title = op.title;
      page.ops.push({
        promptId: op.promptId, sessionId: op.sessionId, endpointId: op.endpointId,
        endpointName: op.endpointName, ts: op.ts, engine: op.engine, prompt: op.prompt || null,
        content: op.content || null, contentTruncated: Boolean(op.contentTruncated), ok: op.ok,
      });
      if (op.ok) {
        page.fetches += 1;
        page.sessions.add(op.sessionId);
        bump(page.readers, op.endpointId, op.endpointName);
        fetches += 1;
        actor.fetches += 1;
        if (op.host) actor.hosts.add(op.host);
      }
    } else {
      const qkey = `${op.engine}\u0000${op.query}`;
      let q = queries.get(qkey);
      if (!q) {
        q = { query: op.query, engine: op.engine, resultCount: 0, results: [], readers: new Map(), sessions: new Set(), resultUrls: new Set() };
        queries.set(qkey, q);
      }
      if (op.ok) {
        searches += 1;
        actor.searches += 1;
        q.sessions.add(op.sessionId);
        bump(q.readers, op.endpointId, op.endpointName);
        if (op.results.length > q.results.length) {
          q.results = op.results;
          q.resultCount = op.resultCount;
        }
        for (const r of op.results) q.resultUrls.add(r.url);
      }
    }
  }

  // Provenance edge (search → fetch): a query "led to" a page when one of its
  // result URLs was actually fetched in the window. Annotate both sides.
  for (const q of queries.values()) {
    q.leadTo = [];
    for (const url of q.resultUrls) {
      if (pages.has(url)) {
        q.leadTo.push(url);
        pages.get(url).viaQueries.add(q.query);
      }
    }
  }

  const pageList = [...pages.values()].map((p) => ({
    url: p.url, host: p.host, title: p.title,
    fetches: p.fetches,
    sessions: p.sessions.size,
    readers: [...p.readers.values()].sort((a, b) => b.n - a.n),
    viaQueries: [...p.viaQueries],
    ops: [...p.ops].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0)),
    // Drift: distinct non-empty contents observed for one URL ⇒ the page
    // changed between reads. Cheap exact-text check (reconstructFile does the
    // per-line version work in the detail view).
    drift: distinctContents(p.ops) > 1,
  }));
  // Most-read pages first, then most cross-session (broadly-consulted sources
  // are the higher-signal ones).
  pageList.sort((a, b) => (b.fetches - a.fetches) || (b.sessions - a.sessions) || a.url.localeCompare(b.url));

  const hostGroups = new Map();
  for (const p of pageList) {
    const key = p.host || "";
    let g = hostGroups.get(key);
    if (!g) {
      g = { host: p.host || null, pages: [], fetches: 0 };
      hostGroups.set(key, g);
    }
    g.pages.push(p.url);
    g.fetches += p.fetches;
  }
  // A search's result hosts too, so a query that returned a domain never
  // fetched still surfaces the source.
  const hostSearches = new Map();
  for (const q of queries.values()) {
    for (const r of q.results) {
      const h = hostOf(r.url) || "";
      hostSearches.set(h, (hostSearches.get(h) || 0) + 1);
    }
  }
  // Union of fetched-page hosts and search-result hosts: a host seen ONLY in
  // results (never fetched) still gets an entry, with an empty page list and
  // fetches:0 — otherwise the "surface the source" promise above is a lie.
  const hostKeys = new Set([...hostGroups.keys(), ...hostSearches.keys()]);
  const hostList = [...hostKeys].map((key) => {
    const g = hostGroups.get(key);
    return {
      host: g ? g.host : (key || null),
      pages: g ? g.pages : [],
      fetches: g ? g.fetches : 0,
      searches: hostSearches.get(key) || 0,
    };
  });
  hostList.sort((a, b) => {
    if (!a.host !== !b.host) return a.host ? -1 : 1;
    return (b.fetches - a.fetches) || (b.pages.length - a.pages.length) || String(a.host).localeCompare(String(b.host));
  });

  const queryList = [...queries.values()]
    .filter((q) => q.sessions.size > 0)
    .map((q) => ({
      query: q.query, engine: q.engine, resultCount: q.resultCount, results: q.results,
      sessions: q.sessions.size,
      readers: [...q.readers.values()].sort((a, b) => b.n - a.n),
      leadTo: q.leadTo,
    }));
  queryList.sort((a, b) => (b.leadTo.length - a.leadTo.length) || (b.sessions - a.sessions) || String(a.query).localeCompare(String(b.query)));

  const actorList = [...actors.values()]
    .filter((a) => a.fetches + a.searches > 0)
    .map((a) => ({ id: a.id, name: a.name, fetches: a.fetches, searches: a.searches, hosts: a.hosts.size, sessions: a.sessions.size }));
  actorList.sort((a, b) => (b.fetches - a.fetches) || (b.searches - a.searches) || a.name.localeCompare(b.name));

  return {
    hosts: hostList,
    pages: pageList,
    queries: queryList,
    actors: actorList,
    totals: {
      pages: pageList.length,
      hosts: hostList.filter((h) => h.host).length,
      fetches, searches,
      queries: queryList.length,
      actors: actorList.length,
    },
  };
}

function distinctContents(ops) {
  const set = new Set();
  for (const o of ops || []) {
    if (o.ok && o.content) set.add(o.content.trim());
  }
  return set.size;
}

// ── Document for the Files-style renderer ────────────────────────────────────
// A fetched page has no writes — the agent never authors it — so every fetch is
// a READ observation. Mapping onto traceFileTree's op shape lets reconstructFile
// render the content the agent saw, and, when two fetches disagree, mark a
// "drift" version: the page CHANGED between reads (a different agent, a later
// session, or a paywall/redirect). Feed the returned ops to reconstructFile().
export function pageDoc(pageOps) {
  const ops = [];
  for (const op of pageOps || []) {
    if (!op.content) continue;
    const lines = op.content.split("\n");
    ops.push({
      kind: "read", ok: op.ok,
      obs: lines.map((t, i) => [i + 1, t]),
      // A capped fetch can't pin the document's length — same contract as a
      // truncated file read.
      obsFull: !op.contentTruncated,
      readLines: lines.length,
      promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
    });
  }
  return ops;
}
